<script setup lang="ts">
import { onBeforeUnmount, ref } from 'vue'
import CanvasCropperLite from './CanvasCropperLite.vue'

const props = withDefaults(
  defineProps<{
    src: string
    disabled?: boolean
    grayscale?: boolean
    rotation?: number
    coordinates?: { left: number; top: number; width: number; height: number } | null
    stencilProps?: { aspectRatio?: number; geometry?: any; guides?: 'full' | 'cut-only' | 'off'; whiteBorder?: boolean; frame2d?: any }
    maxDevicePixelRatio?: number
    overlayOnly?: boolean
    imageOnly?: boolean
    imageWidth?: number
    imageHeight?: number
    changeThrottleMs?: number
  }>(),
  {
    disabled: false,
    maxDevicePixelRatio: 2,
    overlayOnly: false,
    imageOnly: false,
    changeThrottleMs: 90,
  },
)

const emit = defineEmits<{
  (e: 'ready', payload: any): void
  (e: 'change', payload: any): void
  (e: 'complete', payload: any): void
  (e: 'tap', event: PointerEvent): void
}>()

const innerRef = ref<any>(null)
let latestChangePayload: any = null
let lastChangeAt = 0
let changeTimer: ReturnType<typeof setTimeout> | null = null

function flushChange() {
  if (!latestChangePayload) return
  lastChangeAt = Date.now()
  const payload = latestChangePayload
  latestChangePayload = null
  emit('change', payload)
}

function scheduleChangeEmit(payload: any) {
  latestChangePayload = payload
  if (props.imageOnly || props.disabled) return
  const throttleMs = Math.max(16, Number(props.changeThrottleMs || 90) || 90)
  const waitMs = Math.max(0, throttleMs - (Date.now() - lastChangeAt))
  if (changeTimer) return
  if (waitMs <= 0) {
    flushChange()
    return
  }
  changeTimer = setTimeout(() => {
    changeTimer = null
    flushChange()
  }, waitMs)
}

function onInnerChange(payload: any) {
  scheduleChangeEmit(payload)
}

function onInnerComplete(payload: any) {
  if (changeTimer) {
    clearTimeout(changeTimer)
    changeTimer = null
  }
  latestChangePayload = null
  emit('complete', payload)
}

function setCoordinates(coords: any) {
  innerRef.value?.setCoordinates?.(coords)
}

function setImageTransforms(transforms: any) {
  innerRef.value?.setImageTransforms?.(transforms)
}

function setTransforms(transforms: any) {
  innerRef.value?.setTransforms?.(transforms)
}

function setRotation(rotation: number) {
  innerRef.value?.setRotation?.(rotation)
}

defineExpose({
  setCoordinates,
  setImageTransforms,
  setTransforms,
  setRotation,
})

onBeforeUnmount(() => {
  if (changeTimer) clearTimeout(changeTimer)
})
</script>

<template>
  <CanvasCropperLite
    ref="innerRef"
    :src="src"
    :disabled="disabled"
    :grayscale="grayscale"
    :rotation="rotation"
    :coordinates="coordinates"
    :stencil-props="stencilProps"
    :max-device-pixel-ratio="maxDevicePixelRatio"
    :overlay-only="overlayOnly"
    :image-only="imageOnly"
    :image-width="imageWidth"
    :image-height="imageHeight"
    @ready="emit('ready', $event)"
    @change="onInnerChange"
    @complete="onInnerComplete"
    @tap="emit('tap', $event)"
  />
</template>
